import {
  pointFor,
  segmentLength,
  type HumanoidModel,
  type PointKey,
  type SegmentKey,
  type Side,
} from '../hs1/model'
import type { PlmAssembly, PlmPart } from './contract'

/**
 * Vom Einzelteil zur Segmentmasse — der Rückweg aus dem CAD in die Physik.
 *
 * WAS HIER PASSIERT, IN EINEM SATZ: aus vielen Teilen mit je Masse, Schwerpunkt
 * und Trägheitstensor wird ein Ersatzkörper je Segment, und der wird in die
 * Grössen umgerechnet, in denen der Rechenkern denkt.
 *
 * DIE UMRECHNUNG IST DER HEIKLE TEIL, und sie ist zweimal falsch zu machen:
 *
 *   · Der SCHWERPUNKT des Modells ist ein ANTEIL DER SEGMENTLÄNGE, gemessen vom
 *     proximalen Gelenk. Aus dem PLM kommt eine absolute Koordinate. Die
 *     Umrechnung ist die Projektion auf die Segmentachse — nicht der Abstand,
 *     denn ein Schwerpunkt, der seitlich neben der Achse liegt, sitzt der Länge
 *     nach trotzdem in der Mitte.
 *
 *   · Der TRÄGHEITSRADIUS ist ebenfalls ein Anteil der Segmentlänge und bezieht
 *     sich auf den SCHWERPUNKT des Segments. Die Tensoren der Teile beziehen
 *     sich auf ihre eigenen Schwerpunkte; zusammenzufassen sind sie mit dem
 *     Satz von Steiner, und der braucht den Abstand SENKRECHT zur Drehachse.
 *     Wer die Tensoren einfach addiert, unterschlägt genau den Anteil, der bei
 *     einem ausgedehnten Segment der grössere ist.
 *
 * WELCHE ACHSE. Das Gangmodell dreht die Segmente in der Sagittalebene, also um
 * die Querachse y. Der Trägheitsradius wird deshalb aus `Iyy` gebildet. Die
 * beiden anderen Komponenten kommen mit und werden angezeigt; gerechnet wird
 * mit ihnen nicht, weil kein Schritt der inversen Dynamik nach ihnen fragt.
 *
 * DIE SCHÄTZUNG BLEIBT STEHEN. `applyPlm` gibt ein Modell zurück, in dem die
 * gemessenen Werte gelten; der Vergleich gegen den Ausgangsstand steht in
 * `plmCompare` und wird angezeigt, nicht verrechnet.
 */

/** Das proximale Gelenk eines Segments — der Bezugspunkt des Schwerpunktanteils. */
const PROXIMAL: Record<SegmentKey, PointKey | null> = {
  thigh: 'hipN',
  shank: 'knee',
  foot: 'ankle',
  upperArm: 'shoulder',
  forearm: 'elbow',
  trunk: 'pelvis',
  head: 'headC',
  payload: null,
}

/** Das distale Ende — zusammen mit dem proximalen die Segmentachse. */
const DISTAL: Record<SegmentKey, PointKey | null> = {
  thigh: 'knee',
  shank: 'ankle',
  foot: 'mtp',
  upperArm: 'elbow',
  forearm: 'wrist',
  trunk: 'shoulder',
  head: null,
  payload: null,
}

/** Was aus den Teilen eines Segments wird. */
export interface SegmentRollup {
  segment: SegmentKey
  side: Side | null
  parts: number
  /** Summe der Teilemassen, kg. */
  mass: number
  /** Gemeinsamer Schwerpunkt im Konstruktionsrahmen, mm. */
  cog: { x: number; y: number; z: number }
  /** Trägheitsmoment um die Querachse durch den gemeinsamen Schwerpunkt, kg·m². */
  inertiaY: number
  /** Um die Längs- und die Hochachse — mitgeführt, nicht gerechnet. */
  inertiaX: number
  inertiaZ: number
  /** Schwerpunktlage als Anteil der Segmentlänge, vom proximalen Gelenk. */
  com: number
  /** Trägheitsradius als Anteil der Segmentlänge. */
  gyration: number
  /** Wie weit der Schwerpunkt neben der Segmentachse liegt, mm. */
  offAxis: number
  /** Reifegrad: das SCHWÄCHSTE Teil bestimmt ihn. */
  state: PlmPart['state']
}

const norm = (v: { x: number; y: number; z: number }): number => Math.hypot(v.x, v.y, v.z)

/**
 * Fasst die Teile eines Segments zu einem Ersatzkörper zusammen.
 *
 * Bei paarigen Segmenten wird EINE Seite gerollt: das Modell führt die Masse je
 * Seite, nicht die Summe beider. Kommen nur Teile für eine Seite zurück, gilt
 * sie für beide — das ist die Annahme, unter der auch der Punktsatz arbeitet,
 * und sie steht in der Oberfläche.
 */
export function rollupSegment(
  model: HumanoidModel,
  parts: PlmPart[],
  segment: SegmentKey,
  side: Side = 'R',
): SegmentRollup | null {
  const own = parts.filter(
    (p) => p.segment === segment && (p.side === null || p.side === side),
  )
  if (own.length === 0) return null

  let mass = 0
  const cog = { x: 0, y: 0, z: 0 }
  for (const part of own) {
    mass += part.mass
    cog.x += part.mass * part.cog.x
    cog.y += part.mass * part.cog.y
    cog.z += part.mass * part.cog.z
  }
  if (mass <= 0) return null
  cog.x /= mass
  cog.y /= mass
  cog.z /= mass

  /*
    STEINER. Der Abstand zählt SENKRECHT zur Drehachse: für die Querachse y ist
    das der Abstand in der x-z-Ebene. Millimeter in Meter, weil die Tensoren in
    kg·m² kommen.
  */
  let inertiaX = 0
  let inertiaY = 0
  let inertiaZ = 0
  for (const part of own) {
    const dx = (part.cog.x - cog.x) / 1000
    const dy = (part.cog.y - cog.y) / 1000
    const dz = (part.cog.z - cog.z) / 1000
    inertiaX += part.inertia.xx + part.mass * (dy * dy + dz * dz)
    inertiaY += part.inertia.yy + part.mass * (dx * dx + dz * dz)
    inertiaZ += part.inertia.zz + part.mass * (dx * dx + dy * dy)
  }

  const length = segmentLength(model, segment)
  const proximalKey = PROXIMAL[segment]
  const distalKey = DISTAL[segment]

  let com = 0
  let offAxis = 0
  if (proximalKey) {
    const a = pointFor(model, proximalKey, side)
    const arm = { x: cog.x - a.x, y: cog.y - a.y, z: cog.z - a.z }

    if (distalKey) {
      /* Die Projektion auf die Segmentachse. Der Abstand allein wäre falsch:
         ein Schwerpunkt, der 40 mm neben der Achse sitzt, liegt der LÄNGE nach
         trotzdem dort, wo er liegt. */
      const b = pointFor(model, distalKey, side)
      const axis = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }
      const span = norm(axis)
      if (span > 0) {
        const along = (arm.x * axis.x + arm.y * axis.y + arm.z * axis.z) / span
        com = along / (1000 * length)
        offAxis = Math.sqrt(Math.max(0, norm(arm) ** 2 - along ** 2))
      }
    } else {
      /* Kopf und andere Segmente ohne distales Gelenk: der Abstand ist alles,
         was es gibt. */
      com = norm(arm) / (1000 * length)
    }
  }

  /* Trägheitsradius: √(I/m), auf die Segmentlänge bezogen — dieselbe Zählweise
     wie im Modell. */
  const gyration = length > 0 ? Math.sqrt(inertiaY / mass) / length : 0

  const order: PlmPart['state'][] = ['entwurf', 'in_pruefung', 'freigegeben']
  const state = own.reduce<PlmPart['state']>(
    (worst, part) => (order.indexOf(part.state) < order.indexOf(worst) ? part.state : worst),
    'freigegeben',
  )

  return {
    segment,
    side: own.every((p) => p.side === null) ? null : side,
    parts: own.length,
    mass,
    cog,
    inertiaX,
    inertiaY,
    inertiaZ,
    com,
    gyration,
    offAxis,
    state,
  }
}

/** Alle Segmente, für die das PLM Teile geliefert hat. */
export function rollup(model: HumanoidModel, assembly: PlmAssembly): SegmentRollup[] {
  const segments = [...new Set(assembly.parts.map((p) => p.segment))]
  const out: SegmentRollup[] = []
  for (const segment of segments) {
    const entry = rollupSegment(model, assembly.parts, segment)
    if (entry) out.push(entry)
  }
  return out
}

/* -------------------------------------------------------------------------
   Der Vergleich
   ------------------------------------------------------------------------- */

/**
 * Schätzung gegen Messung, je Segment.
 *
 * DAS IST DER EIGENTLICHE ERTRAG DER ANBINDUNG. Ein übernommener Wert allein
 * sagt nur, wie schwer ein Bauteil ist. Die Abweichung sagt, wie weit die
 * anthropometrische Annahme neben der Konstruktion liegt — und damit, wie viel
 * jede Zahl wert war, die auf ihr beruhte.
 */
export interface PlmDelta {
  segment: SegmentKey
  label: string
  /** Der bisherige Wert aus dem Modell. */
  estimated: { kg: number; com: number; gyration: number }
  /** Was aus dem PLM kommt. */
  measured: { kg: number; com: number; gyration: number }
  /** Gemessen minus geschätzt, kg. */
  deltaKg: number
  /** Als Anteil des geschätzten Werts. */
  relative: number
  parts: number
  state: PlmPart['state']
}

export function plmCompare(model: HumanoidModel, assembly: PlmAssembly): PlmDelta[] {
  const out: PlmDelta[] = []
  for (const entry of rollup(model, assembly)) {
    const item = model.masses[entry.segment]
    if (!item) continue
    out.push({
      segment: entry.segment,
      label: item.label,
      estimated: { kg: item.kg, com: item.com, gyration: item.gyration },
      measured: { kg: entry.mass, com: entry.com, gyration: entry.gyration },
      deltaKg: entry.mass - item.kg,
      relative: item.kg > 0 ? (entry.mass - item.kg) / item.kg : 0,
      parts: entry.parts,
      state: entry.state,
    })
  }
  return out
}

/**
 * Legt die gemessenen Werte über ein Modell.
 *
 * Wie `applyEdits`: kopiert nur, was berührt wird, und lässt den Ausgangsstand
 * unangetastet. Segmente ohne Teile bleiben bei ihrer Schätzung — eine
 * Anbindung, die fehlende Daten mit Nullen füllte, machte aus einer Teilmenge
 * einen Roboter ohne Beine.
 */
export function applyPlm(model: HumanoidModel, assembly: PlmAssembly | null): HumanoidModel {
  if (!assembly || assembly.parts.length === 0) return model

  const entries = rollup(model, assembly)
  if (entries.length === 0) return model

  const masses = { ...model.masses }
  for (const entry of entries) {
    const item = masses[entry.segment]
    if (!item) continue
    masses[entry.segment] = {
      ...item,
      kg: entry.mass,
      com: entry.com,
      gyration: entry.gyration,
    }
  }

  return { ...model, id: `${model.id}@${assembly.revision}`, masses }
}
